import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { imageUrl } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { IDS } from "@/constants/testIds";
import { ChevronLeft, ChevronRight, ChevronLeft as ChevLeft, RefreshCw, Trash2, CheckCircle2 } from "lucide-react";

const daysCopy = (d) => {
  if (d === 0) return "Today";
  if (d === 1) return "Tomorrow";
  if (d > 1) return `in ${d} days`;
  return `${Math.abs(d)}d ago`;
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [regening, setRegening] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${id}`);
      setEvent(data);
      // sync to chosen option if any
      if (data.chosen_option_id) {
        const i = data.lookbook.findIndex((o) => o.option_id === data.chosen_option_id);
        if (i >= 0) setIdx(i);
      }
    } catch (err) {
      console.error("event load failed", err);
      toast.error("Event not found");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const total = event?.lookbook?.length || 0;
  const current = event?.lookbook?.[idx];
  const chosen = event?.chosen_option_id === current?.option_id;

  const goto = (nextIdx) => {
    if (!scrollRef.current || total === 0) return;
    const clamped = Math.max(0, Math.min(total - 1, nextIdx));
    setIdx(clamped);
    const child = scrollRef.current.children[clamped];
    if (child) child.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const onScroll = () => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    const nextIdx = Math.round(scrollRef.current.scrollLeft / w);
    if (nextIdx !== idx) setIdx(nextIdx);
  };

  const choose = async () => {
    if (!current) return;
    setChoosing(true);
    try {
      await api.post(`/events/${id}/choose`, { option_id: current.option_id });
      await load();
      toast.success("Outfit locked in.");
    } finally {
      setChoosing(false);
    }
  };

  const regen = async () => {
    setRegening(true);
    try {
      await api.post(`/events/${id}/regenerate`);
      await load();
      setIdx(0);
      toast.success("New lookbook drafted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate");
    } finally {
      setRegening(false);
    }
  };

  const del = async () => {
    if (!confirm("Delete this event? Any wear history stays intact.")) return;
    await api.delete(`/events/${id}`);
    toast.success("Event deleted");
    navigate("/events", { replace: true });
  };

  if (loading || !event) {
    return (
      <div className="min-h-[100dvh] bg-[#FBF9F6] grain flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.3em] text-stone-400">Loading</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0.75rem))" }}>
      <div className="max-w-md mx-auto px-6 pt-12">
        <button onClick={() => navigate("/events")} className="mb-4 flex items-center gap-2 text-sm font-[Manrope] text-stone-600">
          <ChevLeft size={16} /> back
        </button>
        <BrandMark className="mb-4" />
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-1">
          {event.occasion} · {daysCopy(event.days_until)}
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900">
          {event.name}
        </h1>
        <div className="mt-2 text-xs font-[Manrope] text-stone-500 flex flex-wrap gap-x-3 gap-y-1">
          <span>{event.date}</span>
          {event.with_whom && <span>· with {event.with_whom}</span>}
          {event.dress_code && <span>· dress code: {event.dress_code}</span>}
        </div>
        {event.weather_summary && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-stone-200/60 text-xs font-[Manrope] text-stone-600">
            Forecast: {event.weather_summary}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500">
            Option {idx + 1} of {total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => goto(idx - 1)}
              disabled={idx === 0}
              className="h-9 w-9 rounded-full border border-stone-200 flex items-center justify-center text-stone-700 disabled:opacity-30 bg-white"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => goto(idx + 1)}
              disabled={idx >= total - 1}
              className="h-9 w-9 rounded-full border border-stone-200 flex items-center justify-center text-stone-700 disabled:opacity-30 bg-white"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* swipeable lookbook */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="mt-4 flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar max-w-md mx-auto"
      >
        {event.lookbook.map((opt, i) => (
          <OptionCard key={opt.option_id} option={opt} idx={i} isChosen={event.chosen_option_id === opt.option_id} />
        ))}
      </div>

      <div className="max-w-md mx-auto px-6 mt-6 flex flex-col gap-3">
        <button
          onClick={choose}
          disabled={choosing || chosen}
          data-testid={IDS.eventChooseBtn(idx)}
          className={`w-full rounded-full py-4 font-[Manrope] font-medium tracking-wide flex items-center justify-center gap-2 transition-colors ${
            chosen
              ? "bg-stone-900 text-white"
              : "bg-[#6B7A54] hover:bg-[#556141] text-white shadow-[0_16px_40px_rgba(107, 122, 84,0.2)]"
          }`}
        >
          <CheckCircle2 size={16} strokeWidth={1.5} />
          {chosen ? "Locked in — you're set" : "Lock in this look"}
        </button>
        <div className="flex gap-2">
          <button
            data-testid={IDS.eventRegenBtn}
            onClick={regen}
            disabled={regening}
            className="flex-1 py-3 rounded-full border border-stone-300 text-stone-800 font-[Manrope] text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={14} strokeWidth={1.5} className={regening ? "animate-spin" : ""} />
            Regenerate
          </button>
          <button
            data-testid={IDS.eventDeleteBtn}
            onClick={del}
            className="flex-1 py-3 rounded-full border border-stone-300 text-stone-600 font-[Manrope] text-sm flex items-center justify-center gap-2"
          >
            <Trash2 size={14} strokeWidth={1.5} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function OptionCard({ option, idx, isChosen }) {
  return (
    <div
      data-testid={IDS.eventOptionCard(idx)}
      className="snap-center shrink-0 w-full px-6"
    >
      <div className={`bg-white rounded-3xl overflow-hidden border shadow-[0_16px_40px_rgba(28,25,23,0.08)] ${isChosen ? "border-[#6B7A54]" : "border-stone-100"}`}>
        <div className="grid grid-cols-2 gap-[2px] bg-stone-100">
          {(option.items || []).map((it) => (
            <div key={it.item_id} className="aspect-[3/4] bg-white overflow-hidden">
              <img src={imageUrl(it.storage_path)} alt={it.category} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="p-5">
          <div className="inline-block text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-[#6B7A54] bg-[#6B7A54]/10 rounded-full px-3 py-1">
            {option.vibe}
          </div>
          <p className="mt-3 font-[Cormorant_Garamond] text-lg leading-snug italic text-stone-900">
            &ldquo;{option.why}&rdquo;
          </p>
          {isChosen && (
            <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-[Manrope] font-semibold tracking-wider uppercase text-[#6B7A54]">
              <CheckCircle2 size={12} strokeWidth={1.8} /> Chosen
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
